// packages/web-ui/src/panels/chat-panel/ToolCallBox.tsx
//
// W2-S5 AC #6 — Tool-call box with the four states (running / ok / error /
// cancelled), expand/collapse, right-click menu (Copy input / Copy output /
// Re-run), Retry button on error, "Open in syside IDE" hint when input.path
// matches `*.sysml`.

import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  Loader2,
  MinusCircle,
  Sparkles,
  X as XIcon,
} from 'lucide-react';
import type { ToolCallEntry } from '../../state/chat.js';

export interface ToolCallBoxProps {
  entry: ToolCallEntry;
  onRetry?: (callId: string) => void;
  onRerun?: (callId: string) => void;
  openInSyside?: (path: string) => void;
}

interface StatusVisual {
  icon: JSX.Element;
  label: string;
  borderClass: string;
}

function visualFor(status: ToolCallEntry['status'], reducedMotion: boolean): StatusVisual {
  switch (status) {
    case 'running':
      return {
        icon: reducedMotion ? (
          <Circle size={16} className="text-taupe-700" />
        ) : (
          <Loader2 size={16} className="animate-spin text-taupe-700" />
        ),
        label: 'Running…',
        borderClass: 'border-taupe-500',
      };
    case 'ok':
      return {
        icon: <Check size={16} className="text-accent-green" />,
        label: 'OK',
        borderClass: 'border-taupe-500',
      };
    case 'error':
      return {
        icon: <XIcon size={16} className="text-accent-red" />,
        label: 'Error',
        borderClass: 'border-accent-red',
      };
    case 'cancelled':
      return {
        icon: <MinusCircle size={16} className="text-taupe-700" />,
        label: 'Cancelled',
        borderClass: 'border-soft',
      };
  }
}

function formatDuration(startedAt: string, completedAt: string | null, now: number): string {
  const start = new Date(startedAt).getTime();
  const end = completedAt !== null ? new Date(completedAt).getTime() : now;
  const ms = Math.max(0, end - start);
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}m ${s}s`;
}

function usePrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function looksLikeSysmlPath(input: unknown): string | null {
  if (input === null || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const p = obj.path;
  if (typeof p === 'string' && p.toLowerCase().endsWith('.sysml')) return p;
  return null;
}

/**
 * Recognise a `load_skill` tool call so the header can render a "Skill ·
 * <name>" badge instead of the raw tool name. The orchestrator's
 * `load_skill` tool takes `{ name: string }` (see
 * packages/base/src/agent/tools/kb-tools/load-skill.ts) — anything else
 * falls through to the regular tool-call variant.
 */
function skillNameFromEntry(entry: ToolCallEntry): string | null {
  if (entry.toolName !== 'load_skill') return null;
  if (entry.input === null || typeof entry.input !== 'object') return null;
  const obj = entry.input as Record<string, unknown>;
  if (typeof obj.name !== 'string' || obj.name.length === 0) return null;
  return obj.name;
}

export function ToolCallBox({
  entry,
  onRetry,
  onRerun,
  openInSyside,
}: ToolCallBoxProps): JSX.Element {
  const reducedMotion = usePrefersReducedMotion();
  // Every tool call — including errors — renders collapsed by default. The
  // red border + sr-only aria-live announcement already surface a failure;
  // the user expands the box when they want input/error detail. (Auto-
  // expanding errors was removed per RB feedback — it created chat noise.)
  const [expanded, setExpanded] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [tick, setTick] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  // Re-render every 100 ms while running so the duration ticks.
  useEffect(() => {
    if (entry.status !== 'running') return;
    const id = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [entry.status]);

  // Close context menu on outside click / Esc.
  useEffect(() => {
    if (menu === null) return;
    function onClick(e: globalThis.MouseEvent): void {
      if (menuRef.current?.contains(e.target as Node)) return;
      setMenu(null);
    }
    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Escape') setMenu(null);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const visual = visualFor(entry.status, reducedMotion);
  const sysmlPath = looksLikeSysmlPath(entry.input);
  const skillName = skillNameFromEntry(entry);
  const duration = formatDuration(
    entry.startedAt,
    entry.completedAt ?? null,
    // Tick included to invalidate memoisation while running.
    Date.now() - 0 * tick,
  );

  const onContextMenu = (e: MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'F10' && e.shiftKey) {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      setMenu({ x: rect.left, y: rect.bottom });
    }
  };

  const copy = async (text: string): Promise<void> => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div
      data-testid="tool-call-box"
      data-status={entry.status}
      data-variant={skillName !== null ? 'skill' : 'tool'}
      onContextMenu={onContextMenu}
      className={`bg-cream-50 my-2 overflow-hidden rounded-none border ${visual.borderClass}`}
    >
      <button
        type="button"
        data-testid="tool-call-header"
        aria-expanded={expanded}
        aria-controls={`tool-body-${entry.callId}`}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={onTriggerKeyDown}
        className="flex h-7 w-full items-center gap-2 px-3 py-1 text-left"
      >
        <span aria-hidden="true">{visual.icon}</span>
        <span className="sr-only">{visual.label}</span>
        {skillName !== null ? (
          <span
            data-testid="tool-call-skill-badge"
            className="flex items-center gap-1 font-mono text-xs"
          >
            <Sparkles size={12} className="text-audi-red" aria-hidden="true" />
            <span className="font-semibold">Skill</span>
            <span className="text-taupe-700" aria-hidden="true">·</span>
            <span data-testid="tool-call-skill-name">{skillName}</span>
          </span>
        ) : (
          <span className="font-mono text-xs">{entry.toolName}</span>
        )}
        <span data-testid="tool-call-status-label" className="text-xs">
          {visual.label}
        </span>
        <span className="ml-auto flex items-center gap-2 text-xs text-taupe-700">
          <span data-testid="tool-call-duration">⏱ {duration}</span>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </button>

      {entry.status === 'error' ? (
        <div
          role="alert"
          aria-live="assertive"
          data-testid="tool-call-error-announce"
          className="sr-only"
        >
          Tool {entry.toolName} failed. {entry.errorMessage ?? 'unknown error'}
        </div>
      ) : null}

      {expanded ? (
        <div
          id={`tool-body-${entry.callId}`}
          data-testid="tool-call-body"
          className="border-t border-soft px-3 py-2"
        >
          <dl className="space-y-2 text-xs">
            <dt className="font-semibold">Input</dt>
            <dd>
              <pre
                className="whitespace-pre-wrap font-mono text-xs"
                data-testid="tool-call-input"
              >
                {JSON.stringify(entry.input, null, 2)}
              </pre>
            </dd>
            {entry.status === 'ok' && entry.output !== undefined ? (
              <>
                <dt className="font-semibold">Output</dt>
                <dd>
                  <pre
                    className="whitespace-pre-wrap font-mono text-xs"
                    data-testid="tool-call-output"
                  >
                    {JSON.stringify(entry.output, null, 2)}
                  </pre>
                </dd>
              </>
            ) : null}
            {entry.status === 'error' ? (
              <>
                <dt className="font-semibold">Error</dt>
                <dd className="text-accent-red" data-testid="tool-call-error">
                  {entry.errorMessage ?? 'unknown error'}
                </dd>
                <dd className="flex gap-2 pt-2">
                  <button
                    type="button"
                    data-testid="tool-call-retry"
                    onClick={() => onRetry?.(entry.callId)}
                    className="rounded bg-accent-red-bg px-2 py-1 text-accent-red"
                  >
                    Retry
                  </button>
                  {sysmlPath !== null ? (
                    <button
                      type="button"
                      data-testid="tool-call-open-syside"
                      onClick={() => openInSyside?.(sysmlPath)}
                      className="rounded border border-taupe-500 px-2 py-1"
                    >
                      Open in syside IDE
                    </button>
                  ) : null}
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}

      {menu !== null ? (
        <div
          ref={menuRef}
          role="menu"
          data-testid="tool-call-context-menu"
          style={{ position: 'fixed', left: menu.x, top: menu.y }}
          className="border-taupe-500 bg-cream-50 z-30 min-w-[180px] rounded-none border py-1"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="tool-call-copy-input"
            onClick={async () => {
              await copy(JSON.stringify(entry.input, null, 2));
              setMenu(null);
            }}
            className="block w-full px-3 py-1 text-left text-sm hover:bg-cream-200"
          >
            Copy input
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={entry.output === undefined}
            data-testid="tool-call-copy-output"
            onClick={async () => {
              if (entry.output === undefined) return;
              await copy(JSON.stringify(entry.output, null, 2));
              setMenu(null);
            }}
            className="block w-full px-3 py-1 text-left text-sm hover:bg-cream-200 disabled:opacity-50"
          >
            Copy output
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid="tool-call-rerun"
            onClick={() => {
              onRerun?.(entry.callId);
              setMenu(null);
            }}
            className="block w-full px-3 py-1 text-left text-sm hover:bg-cream-200"
          >
            Re-run with same input
          </button>
        </div>
      ) : null}
    </div>
  );
}
